import { Button as HeadlessButton } from '@headlessui/react';
import { StarIcon as OutlineStarIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useState } from 'react';
import { cn } from '../../lib/classNameHelpers';

interface FavoriteButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isFavorite: boolean;
  onToggle: () => void;
  iconClassName?: string;
}

/**
 * Star icon to favorite/unfavorite. Tracks hover so in order to not show hover
 * state after clicking.
 */
export const FavoriteButton: React.FC<FavoriteButtonProps> = ({
  isFavorite,
  onToggle,
  className,
  iconClassName,
  onMouseEnter,
  onMouseLeave,
  onClick,
  ...buttonProps
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [hasClicked, setHasClicked] = useState(false);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      onToggle();
      if (isHovering) {
        setHasClicked(true);
      }
      setIsHovering(false);
      onClick?.(e);
    },
    [isHovering, onClick, onToggle]
  );

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (!hasClicked) {
        setIsHovering(true);
      }
      onMouseEnter?.(e);
    },
    [hasClicked, onMouseEnter]
  );

  const handleMouseLeave = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setIsHovering(false);
      setHasClicked(false);
      onMouseLeave?.(e);
    },
    [onMouseLeave]
  );

  const iconColor = isFavorite
    ? isHovering
      ? 'text-contentBlue fill-contentBlue brightness-105'
      : 'text-contentBlue fill-contentBlue'
    : isHovering
      ? 'text-contentSecondary fill-contentSecondary'
      : 'text-contentSecondary fill-transparent';

  return (
    <HeadlessButton
      {...buttonProps}
      className={cn('!p-0', className)}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <OutlineStarIcon className={cn('transition-all', iconColor, iconClassName)} />
    </HeadlessButton>
  );
};
